import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { PORTFOLIO } from "@/data";
import { Reveal, Eyebrow } from "@/components/site/motion-lib";

export default function Portfolio() {
  const [lightbox, setLightbox] = useState(null);
  const filtered = PORTFOLIO;

  return (
    <section id="lavori" data-testid="portfolio-section" className="relative py-24 md:py-32 border-t border-[#F5F1EA]/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-xl">
            <Reveal><Eyebrow>Portfolio</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#F5F1EA] mt-5 text-balance">
                Lavori usciti dalla nostra officina
              </h2>
            </Reveal>
          </div>
        </div>

        <motion.div layout className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <motion.button
                key={p.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => setLightbox(p)}
                data-testid={`portfolio-item-${p.id}`}
                className="group relative aspect-[4/5] overflow-hidden rounded-2xl metal-border text-left"
              >
                <img src={p.img} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#141414]/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                  <span className="eyebrow">{p.cat}</span>
                  <h3 className="font-display text-xl font-bold text-[#F5F1EA] mt-2">{p.title}</h3>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* All works CTA -> socials */}
        <Reveal>
          <div className="mt-20 text-center">
            <div className="flex justify-center"><Eyebrow>Tutti i nostri lavori</Eyebrow></div>
            <p className="mt-5 mx-auto max-w-2xl text-[#F5F1EA]/65 leading-relaxed text-lg">
              Questi qua sopra sono solo alcuni esempi di tutti i lavori che facciamo.
            </p>
            <a
              href="#contatti"
              data-testid="see-all-works"
              className="group mt-8 inline-flex items-center gap-2.5 rounded-full border border-[#C89B4A]/50 px-7 py-3.5 text-base font-semibold text-[#C89B4A] hover:bg-[#C89B4A] hover:text-[#141414] transition-colors duration-300"
            >
              Vedi tutti i nostri lavori
              <ArrowUpRight size={18} strokeWidth={1.75} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
            <p className="mt-4 text-sm text-[#F5F1EA]/45">
              Seguici sui nostri social per vedere tutti i lavori realizzati.
            </p>
          </div>
        </Reveal>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[200] grid place-items-center bg-[#141414]/95 backdrop-blur-md p-5"
          >
            <button aria-label="Chiudi" data-testid="lightbox-close" className="absolute top-6 right-6 grid h-12 w-12 place-items-center rounded-full border border-[#F5F1EA]/20 text-[#F5F1EA] hover:text-[#C89B4A] hover:border-[#C89B4A] transition-colors">
              <X size={22} strokeWidth={1.5} />
            </button>
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full"
            >
              <img src={lightbox.img} alt={lightbox.title} className="w-full max-h-[75vh] object-contain rounded-xl" />
              <div className="mt-4 text-center">
                <span className="eyebrow">{lightbox.cat}</span>
                <h3 className="font-display text-2xl font-bold text-[#F5F1EA] mt-1">{lightbox.title}</h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
